import { useState, useCallback, useEffect } from 'react';

// Hardcoded for development, should come from env in prod
const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''; 

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      checkRegistration();
    }
  }, []);

  const checkRegistration = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      setIsRegistered(!!subscription);
    } catch (e) {
      console.error('Error checking push registration:', e);
    }
  };

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = useCallback(async (token: string) => {
    if (!isSupported) return false;
    if (!PUBLIC_VAPID_KEY) {
      console.warn("No VAPID key configured for push notifications");
      return false;
    }
    
    setIsLoading(true);
    try {
      // Get current registration.
      const registrations = await navigator.serviceWorker.getRegistrations();
      let swRegistration = null;
      if (registrations.length > 0) {
        swRegistration = registrations[0];
      } else {
         setIsLoading(false);
         console.warn("No service worker active");
         return false;
      }
      
      const newSubscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY)
      });
      
      // Send to server
      const response = await fetch('/api/push/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSubscription)
      });
      
      if (response.ok) {
        setIsRegistered(true);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Failed to subscribe the user: ', e);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  return {
    isSupported,
    isRegistered,
    isLoading,
    subscribe
  };
}
